import React, { useState, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { patchCompany } from '../../../api/company/company';
import { useNotification } from '../../../context/NotificationContext';
import { format, parse } from 'date-fns';
import '../pages/business-hours/BusinessHoursLayout.css';
import '../pages/payment-subsettings/PaymentSubsettings.css';
import CopyIcon from '../pages/business-hours/CopyIcon';
import BhModal from '../pages/business-hours/BhModal';
import BhTimeSelect from '../pages/business-hours/BhTimeSelect';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function TimeField({ id, label, value, onDateChange }) {
  return <BhTimeSelect id={id} label={label} value={value} onDateChange={onDateChange} />;
}

const MobileBusinessHours = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData } = useAuth();
  const [companyHours, setCompanyHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setNotification } = useNotification();
  const copyHoursTitleId = useId();
  const copyBreakTitleId = useId();
  const exitTitleId = useId();

  const [copyDialog, setCopyDialog] = useState({
    open: false,
    sourceDay: '',
    hours: { open: null, close: null }
  });
  const [selectedDays, setSelectedDays] = useState({});
  const [breakCopyDialog, setBreakCopyDialog] = useState({
    open: false,
    sourceDay: '',
    breakTimes: {
      breakStart: null,
      breakEnd: null,
      hasBreak: false
    }
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [holidaySettings, setHolidaySettings] = useState({
    openOnHolidays: false
  });

  useEffect(() => {
    fetchCompanyHours();
  }, []);

  const fetchCompanyHours = async () => {
    if (!companyData?.id) return;

    try {
      const storedHours = companyData?.settings?.companyHours;
      const defaultOpenTime = parse('9:00 AM', 'h:mm aa', new Date());
      const defaultCloseTime = parse('5:00 PM', 'h:mm aa', new Date());

      if (storedHours) {
        const processedHours = {};

        daysOfWeek.forEach((day) => {
          const dayKey = day.toLowerCase();
          const existingHours = storedHours[dayKey] || {};

          processedHours[dayKey] = {
            open: existingHours.open ? parse(existingHours.open, 'h:mm aa', new Date()) : defaultOpenTime,
            close: existingHours.close ? parse(existingHours.close, 'h:mm aa', new Date()) : defaultCloseTime,
            isOpen: existingHours.isOpen ?? true,
            hasBreak: existingHours.hasBreak ?? false,
            breakStart: existingHours.breakStart ? parse(existingHours.breakStart, 'h:mm aa', new Date()) : null,
            breakEnd: existingHours.breakEnd ? parse(existingHours.breakEnd, 'h:mm aa', new Date()) : null
          };
        });

        setCompanyHours(processedHours);
      } else {
        const defaultHours = {};
        daysOfWeek.forEach((day) => {
          defaultHours[day.toLowerCase()] = {
            open: defaultOpenTime,
            close: defaultCloseTime,
            isOpen: true,
            hasBreak: false,
            breakStart: null,
            breakEnd: null
          };
        });
        setCompanyHours(defaultHours);
      }

      setHolidaySettings({
        openOnHolidays: companyData?.settings?.openOnHolidays ?? false
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching company hours:', error);
      setLoading(false);
    }
  };

  const handleHoursChange = (day, field, value) => {
    setHasUnsavedChanges(true);
    setCompanyHours((prev) => ({
      ...prev,
      [day.toLowerCase()]: {
        ...prev[day.toLowerCase()],
        [field]: value
      }
    }));
  };

  const persistHoursToFirestore = async () => {
    if (!companyData?.id) throw new Error('No company');
    const formattedHours = {};
    Object.entries(companyHours).forEach(([day, hours]) => {
      formattedHours[day] = {
        open:
          hours.open && hours.open instanceof Date && !isNaN(hours.open)
            ? format(hours.open, 'h:mm aa').toLowerCase()
            : '9:00 am',
        close:
          hours.close && hours.close instanceof Date && !isNaN(hours.close)
            ? format(hours.close, 'h:mm aa').toLowerCase()
            : '5:00 pm',
        isOpen: hours.isOpen ?? true,
        hasBreak: hours.hasBreak ?? false,
        breakStart:
          hours.breakStart && hours.breakStart instanceof Date && !isNaN(hours.breakStart)
            ? format(hours.breakStart, 'h:mm aa').toLowerCase()
            : null,
        breakEnd:
          hours.breakEnd && hours.breakEnd instanceof Date && !isNaN(hours.breakEnd)
            ? format(hours.breakEnd, 'h:mm aa').toLowerCase()
            : null
      };
    });

    const company = await patchCompany({
      companyId: companyData.id,
      company: {
        settings: {
          companyHours: formattedHours,
          openOnHolidays: holidaySettings.openOnHolidays
        }
      }
    });
    updateCompanyData?.({ ...companyData, ...company });
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await persistHoursToFirestore();
      setNotification({
        open: true,
        message: 'Company hours updated successfully',
        severity: 'success'
      });
      setTimeout(() => {
        navigate('/app/overview/settings');
      }, 1500);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving company hours:', error);
      setNotification({
        open: true,
        message: 'Failed to save company hours',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCopyDialog = (day) => {
    setSelectedDays({});
    setCopyDialog({
      open: true,
      sourceDay: day,
      hours: {
        open: companyHours[day.toLowerCase()]?.open,
        close: companyHours[day.toLowerCase()]?.close
      }
    });
  };

  const handleApplyHours = () => {
    const updatedHours = { ...companyHours };
    Object.keys(selectedDays).forEach((dayKey) => {
      if (selectedDays[dayKey] && dayKey !== copyDialog.sourceDay.toLowerCase()) {
        updatedHours[dayKey] = {
          ...updatedHours[dayKey],
          open: copyDialog.hours.open,
          close: copyDialog.hours.close
        };
      }
    });
    setCompanyHours(updatedHours);
    setCopyDialog({ open: false, sourceDay: '', hours: { open: null, close: null } });
    setSelectedDays({});
    setHasUnsavedChanges(true);
  };

  const handleOpenBreakCopyDialog = (day) => {
    setSelectedDays({});
    setBreakCopyDialog({
      open: true,
      sourceDay: day,
      breakTimes: {
        breakStart: companyHours[day.toLowerCase()]?.breakStart,
        breakEnd: companyHours[day.toLowerCase()]?.breakEnd,
        hasBreak: companyHours[day.toLowerCase()]?.hasBreak
      }
    });
  };

  const handleApplyBreakTimes = () => {
    const updatedHours = { ...companyHours };
    Object.keys(selectedDays).forEach((dayKey) => {
      if (selectedDays[dayKey] && dayKey !== breakCopyDialog.sourceDay.toLowerCase()) {
        updatedHours[dayKey] = {
          ...updatedHours[dayKey],
          hasBreak: breakCopyDialog.breakTimes.hasBreak,
          breakStart: breakCopyDialog.breakTimes.breakStart,
          breakEnd: breakCopyDialog.breakTimes.breakEnd
        };
      }
    });
    setCompanyHours(updatedHours);
    setBreakCopyDialog({
      open: false,
      sourceDay: '',
      breakTimes: { breakStart: null, breakEnd: null, hasBreak: false }
    });
    setSelectedDays({});
    setHasUnsavedChanges(true);
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      navigate('/app/overview/settings');
    }
  };

  const handleSaveAndExit = async () => {
    setSaving(true);
    try {
      await persistHoursToFirestore();
      setHasUnsavedChanges(false);
      navigate('/app/overview/settings');
    } catch (error) {
      console.error('Error saving hours:', error);
      setNotification({
        open: true,
        message: 'Failed to save company hours',
        severity: 'error'
      });
    } finally {
      setSaving(false);
      setShowExitDialog(false);
    }
  };

  return (
    <>
      <div className="mobile-business-hours-page bh-mobile-shell">
        <div className="bh-landing">
          <header className="bh-header">
            <h1 className="bh-title">Business hours</h1>
            <p className="bh-description">
              Turn days on or off, set times with the clock control, and add breaks. Copy applies settings to other
              days.
            </p>
          </header>

          <section className="bh-section bh-holiday-wrap">
            <h2 className="bh-section-title">Holidays</h2>
            <p className="bh-section-desc">Stay open for bookings on recognized holidays.</p>
            <label className="ps-option-box" htmlFor="mobile-business-hours-open-holidays">
              <input
                id="mobile-business-hours-open-holidays"
                type="checkbox"
                className="ps-checkbox"
                checked={holidaySettings.openOnHolidays}
                onChange={(e) => {
                  setHolidaySettings((prev) => ({
                    ...prev,
                    openOnHolidays: e.target.checked
                  }));
                  setHasUnsavedChanges(true);
                }}
              />
              <span className="ps-option-box__body">
                <span className="ps-option-box__title">Open on holidays</span>
                <p className="ps-option-box__hint">
                  Tick the box if you accept bookings on recognized holidays.
                </p>
              </span>
            </label>
          </section>

          <section className="bh-section bh-section--divider">
            <h2 className="bh-section-title">Weekly schedule</h2>
            <p className="bh-section-desc">One card per weekday.</p>

            <div className="bh-day-list">
              {daysOfWeek.map((day) => {
                const dayKey = day.toLowerCase();
                const safeDay = day.replace(/\s+/g, '-');
                const isOpen = companyHours[dayKey]?.isOpen;
                const hours = companyHours[dayKey];

                return (
                  <article
                    key={day}
                    className={`bh-day-card ${!isOpen ? 'bh-day-card--closed' : ''}`}
                  >
                    <div className="bh-day-card__header">
                      <h3 className="bh-day-name">{day}</h3>
                      <label className="bh-cell-tick">
                        <input
                          type="checkbox"
                          checked={isOpen || false}
                          onChange={(e) => handleHoursChange(day, 'isOpen', e.target.checked)}
                          aria-label={
                            isOpen ? `${day}: open, tick to close` : `${day}: closed, tick to open`
                          }
                        />
                        <span className={`bh-cell-tick__text ${isOpen ? 'is-open' : 'is-closed'}`}>
                          {isOpen ? 'Open' : 'Closed'}
                        </span>
                      </label>
                    </div>

                    {!isOpen ? (
                      <p className="bh-day-closed-msg">Not accepting bookings this day.</p>
                    ) : (
                      <div className="bh-day-card__body">
                        <div>
                          <div className="bh-block__head">
                            <span className="bh-block__label">Hours</span>
                            <button
                              type="button"
                              className="bh-icon-btn"
                              title="Copy to other days"
                              onClick={() => handleOpenCopyDialog(day)}
                              aria-label={`Copy ${day} hours`}
                            >
                              <CopyIcon />
                            </button>
                          </div>
                          <div className="bh-time-stack">
                            <TimeField
                              id={`m-open-${safeDay}`}
                              label="Open"
                              value={hours?.open}
                              onDateChange={(d) => handleHoursChange(day, 'open', d)}
                            />
                            <TimeField
                              id={`m-close-${safeDay}`}
                              label="Close"
                              value={hours?.close}
                              onDateChange={(d) => handleHoursChange(day, 'close', d)}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="bh-block__head">
                            <span className="bh-block__label">Break</span>
                            {hours?.hasBreak && (
                              <button
                                type="button"
                                className="bh-icon-btn"
                                title="Copy break to other days"
                                onClick={() => handleOpenBreakCopyDialog(day)}
                                aria-label={`Copy ${day} break`}
                              >
                                <CopyIcon />
                              </button>
                            )}
                          </div>
                          <label
                            className="bh-cell-tick bh-cell-tick--single-line"
                            style={{ marginBottom: hours?.hasBreak ? '0.75rem' : 0 }}
                          >
                            <input
                              type="checkbox"
                              checked={hours?.hasBreak || false}
                              onChange={(e) => handleHoursChange(day, 'hasBreak', e.target.checked)}
                              aria-label={`Break on ${day}`}
                            />
                            <span
                              className={`bh-cell-tick__text ${
                                hours?.hasBreak ? 'is-open' : 'is-closed'
                              }`}
                            >
                              Scheduled break
                            </span>
                          </label>

                          {hours?.hasBreak && (
                            <div className="bh-time-stack">
                              <TimeField
                                id={`m-bs-${safeDay}`}
                                label="Break start"
                                value={hours?.breakStart}
                                onDateChange={(d) => handleHoursChange(day, 'breakStart', d)}
                              />
                              <TimeField
                                id={`m-be-${safeDay}`}
                                label="Break end"
                                value={hours?.breakEnd}
                                onDateChange={(d) => handleHoursChange(day, 'breakEnd', d)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <div className="bh-sticky-footer">
        <button type="button" className="bh-btn bh-btn--ghost" onClick={handleBackNavigation} disabled={loading}>
          Cancel
        </button>
        <button type="button" className="bh-btn bh-btn--primary" onClick={saveHours} disabled={loading || saving}>
          {saving ? (
            <>
              <span className="bh-btn-spinner" aria-hidden />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>

      <BhModal
        open={copyDialog.open}
        footerStretch
        onClose={() => {
          setCopyDialog((p) => ({ ...p, open: false }));
          setSelectedDays({});
        }}
        title="Copy business hours"
        titleId={copyHoursTitleId}
        footer={
          <>
            <button
              type="button"
              className="bh-btn bh-btn--ghost"
              onClick={() => {
                setCopyDialog((p) => ({ ...p, open: false }));
                setSelectedDays({});
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bh-btn bh-btn--primary"
              onClick={handleApplyHours}
              disabled={Object.values(selectedDays).filter(Boolean).length === 0}
            >
              Apply
            </button>
          </>
        }
      >
        <p className="bh-modal-intro">Select days:</p>
        <div className="bh-modal-summary">
          <p>
            <strong>Open:</strong> {copyDialog.hours.open && format(copyDialog.hours.open, 'h:mm aa')}
          </p>
          <p>
            <strong>Close:</strong> {copyDialog.hours.close && format(copyDialog.hours.close, 'h:mm aa')}
          </p>
        </div>
        <div className="bh-check-list">
          {daysOfWeek.map((day) =>
            day.toLowerCase() !== copyDialog.sourceDay.toLowerCase() ? (
              <label key={day} className="bh-check-row">
                <input
                  type="checkbox"
                  checked={selectedDays[day.toLowerCase()] || false}
                  onChange={() =>
                    setSelectedDays((prev) => ({
                      ...prev,
                      [day.toLowerCase()]: !prev[day.toLowerCase()]
                    }))
                  }
                />
                {day}
              </label>
            ) : null
          )}
        </div>
      </BhModal>

      <BhModal
        open={breakCopyDialog.open}
        footerStretch
        onClose={() => {
          setBreakCopyDialog((p) => ({ ...p, open: false }));
          setSelectedDays({});
        }}
        title="Copy break times"
        titleId={copyBreakTitleId}
        footer={
          <>
            <button
              type="button"
              className="bh-btn bh-btn--ghost"
              onClick={() => {
                setBreakCopyDialog((p) => ({ ...p, open: false }));
                setSelectedDays({});
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bh-btn bh-btn--primary"
              onClick={handleApplyBreakTimes}
              disabled={Object.values(selectedDays).filter(Boolean).length === 0}
            >
              Apply
            </button>
          </>
        }
      >
        <p className="bh-modal-intro">Select days:</p>
        <div className="bh-modal-summary">
          <p>
            <strong>Start:</strong>{' '}
            {breakCopyDialog.breakTimes.breakStart &&
              format(breakCopyDialog.breakTimes.breakStart, 'h:mm aa')}
          </p>
          <p>
            <strong>End:</strong>{' '}
            {breakCopyDialog.breakTimes.breakEnd &&
              format(breakCopyDialog.breakTimes.breakEnd, 'h:mm aa')}
          </p>
        </div>
        <div className="bh-check-list">
          {daysOfWeek.map((day) =>
            day.toLowerCase() !== breakCopyDialog.sourceDay.toLowerCase() ? (
              <label key={day} className="bh-check-row">
                <input
                  type="checkbox"
                  checked={selectedDays[day.toLowerCase()] || false}
                  onChange={() =>
                    setSelectedDays((prev) => ({
                      ...prev,
                      [day.toLowerCase()]: !prev[day.toLowerCase()]
                    }))
                  }
                />
                {day}
              </label>
            ) : null
          )}
        </div>
      </BhModal>

      <BhModal
        open={showExitDialog}
        footerStretch
        onClose={() => setShowExitDialog(false)}
        title="Unsaved changes"
        titleId={exitTitleId}
        footer={
          <>
            <button type="button" className="bh-btn bh-btn--ghost" onClick={() => navigate('/app/overview/settings')}>
              Leave
            </button>
            <button type="button" className="bh-btn bh-btn--primary" onClick={handleSaveAndExit} disabled={saving}>
              {saving ? (
                <>
                  <span className="bh-btn-spinner" aria-hidden />
                  Saving…
                </>
              ) : (
                'Save & leave'
              )}
            </button>
          </>
        }
      >
        <p className="bh-modal-intro">Save before leaving?</p>
      </BhModal>
    </>
  );
};

export default MobileBusinessHours;
